import { axiosPublic } from 'api/axios'
import {
  normalizeExchange30DayPrices,
  type Exchange30DayPrice,
  type ExchangeOrder,
  type TradingMarketData,
} from 'models/trading'
import type { BmnMinedRow, NavValue } from 'models/priceChart'
import type { StockMarketBar } from 'models/chart'
import type { RawRates } from 'models/investCurrency'

export interface BtcPrice {
  USD: number
  EUR?: number
  time?: string
}

export const fetchBtcPrice = async (): Promise<BtcPrice> =>
  axiosPublic.post<BtcPrice>('/prices/btcPrice').then((response) => response.data)

/** EUR-based rates (`{ rates: { USD: 1.08 } }`) and the BTC price in USD, as the invest flow reads them. */
export const fetchExchangeRates = async (): Promise<RawRates> => {
  const [btc, exchange] = await Promise.all([
    fetchBtcPrice(),
    axiosPublic.post<{ rates?: Record<string, number> }>('/prices/exchangerates').then((response) => response.data),
  ])
  return { btcInUsd: Number(btc?.USD), rates: exchange?.rates ?? {} }
}

export const fetchExchange30DayPrices = async (assetId: string): Promise<Exchange30DayPrice[]> =>
  axiosPublic
    .post<Exchange30DayPrice[]>('/prices/get30DayPrices', { assetId })
    .then((response) => (Array.isArray(response.data) ? response.data : []))

export const fetchExchangeOrders = async (assetId: string): Promise<ExchangeOrder[]> =>
  axiosPublic
    .post<ExchangeOrder[]>('/prices/getOrders', { assetId })
    .then((response) => (Array.isArray(response.data) ? response.data : []))

/** Each half fails soft, so a dead order-book feed never hides the price table (and vice versa). */
export const fetchTradingMarketData = async (assetId: string): Promise<TradingMarketData> => {
  const [prices, orderBook] = await Promise.all([
    fetchExchange30DayPrices(assetId)
      .then(normalizeExchange30DayPrices)
      .catch((error) => {
        console.error('[trading] 30-day prices failed:', error)
        return null
      }),
    fetchExchangeOrders(assetId).catch((error) => {
      console.error('[trading] order book failed:', error)
      return null
    }),
  ])
  return { prices, orderBook }
}

/** A mining note's daily mined BTC and value, by its Liquid asset (`secondaryAssetId`). */
export const fetchBmnMined = async (assetId: string): Promise<BmnMinedRow[]> =>
  axiosPublic
    .post<BmnMinedRow[]>('/prices/bmnMined', { assetId })
    .then((response) => (Array.isArray(response.data) ? response.data : []))

export const fetchNavPrices = async (assetId: string): Promise<NavValue[]> =>
  axiosPublic
    .post<NavValue[]>('/prices/getNAVPrices', { assetId })
    .then((response) => (Array.isArray(response.data) ? response.data : []))

/** Daily bars of a stock, keyed by the price service's ticker (see `priceTicker`). */
export const fetchStockmarketPrices = async (tickerSymbol: string): Promise<StockMarketBar[]> =>
  axiosPublic
    .post<StockMarketBar[]>('/prices/stockmarketPrices', { tickerSymbol })
    .then((response) => (Array.isArray(response.data) ? response.data : []))

/** XAUt's price in `currency` on `day` (`YYYY-MM-DD`; today: the latest). Costly for the backend: ask once. */
export const fetchXautPrice = async (currency: string, day: string): Promise<number | undefined> =>
  axiosPublic
    .post<{ list?: { date: string; price: number }[] }>('/public/prices/xaut', {
      start_date: day,
      end_date: day,
      currency,
    })
    .then((response) => {
      const list = response.data?.list ?? []
      const price = Number(list[list.length - 1]?.price)
      return Number.isFinite(price) && price > 0 ? price : undefined
    })
